import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, CalendarClock, Home, Wallet, Pencil, Trash2, CalendarSync, User as UserIcon, Phone, Target, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useDebouncedValue } from "@/lib/hooks";
import { fmtLocal, todayStr } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { LeadTypeBadge } from "@/components/Badges";
import OpportunityDialog from "@/components/OpportunityDialog";
import RescheduleDialog, { VisitHistory } from "@/components/RescheduleDialog";
import { STATUS_META } from "@/lib/constants";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "bulk", label: "Bulk" },
  { key: "live", label: "Live" },
  { key: "pending", label: "Visit pending" },
  { key: "done", label: "Visit done" },
];

export default function OpportunitiesPage() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [rescheduling, setRescheduling] = useState(null);
  const q = useDebouncedValue(search, 300);

  const oppsQ = useQuery({
    queryKey: ["opportunities", { q, filter }],
    queryFn: async () => {
      const params = {};
      if (q) params.search = q;
      if (filter === "bulk" || filter === "live") params.lead_type = filter;
      if (filter === "pending" || filter === "done") params.visit = filter;
      return (await api.get("/opportunities", { params })).data;
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const items = oppsQ.data?.items || [];

  return (
    <div className="space-y-4" data-testid="opportunities-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-navy lg:text-3xl">Opportunities</h2>
          <p className="mt-1 text-sm text-slate-500">High-intent leads with a site visit in the pipeline.</p>
        </div>
        <div className="text-right" data-testid="opps-total">
          <div className="text-3xl font-black text-navy">{oppsQ.data?.total ?? "—"}</div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400">total</div>
        </div>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="opps-search"
          placeholder="Search by name, phone, project..."
          className="h-12 rounded-xl border-slate-200 bg-white pl-10 text-[15px] shadow-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 no-scrollbar">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`opps-filter-${f.key}`} data-active={filter === f.key} className="chip" onClick={() => setFilter(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {oppsQ.isLoading && Array.from({ length: 3 }).map((_, i) => <div key={i} className="soft-card h-44 animate-pulse bg-slate-50" />)}
        {!oppsQ.isLoading && items.length === 0 && (
          <div className="col-span-full soft-card flex flex-col items-center gap-2 p-10 text-center text-slate-500" data-testid="opps-empty">
            <Target className="h-8 w-8 text-slate-300" />
            No opportunities yet. Open a lead and tap <b className="text-navy">Create Opportunity</b>.
          </div>
        )}
        {items.map((o) => <OppCard key={o.id} opp={o} onEdit={() => setEditing(o)} onReschedule={() => setRescheduling(o)} />)}
      </div>

      <OpportunityDialog open={Boolean(editing)} onOpenChange={(v) => !v && setEditing(null)} opp={editing} />
      <RescheduleDialog opp={rescheduling} onOpenChange={(v) => !v && setRescheduling(null)} />
    </div>
  );
}

function OppCard({ opp, onEdit, onReschedule }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const refresh = () => ["opportunities", "tasks", "dashboard", "leads", "lead"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const toggleMut = useMutation({
    mutationFn: async (v) => api.patch(`/opportunities/${opp.id}`, { visit_done: v }),
    onSuccess: (_, v) => { toast.success(v ? "Visit marked done" : "Visit reopened"); refresh(); },
    onError: () => toast.error("Update failed"),
  });
  const delMut = useMutation({
    mutationFn: async () => api.delete(`/opportunities/${opp.id}`),
    onSuccess: () => { toast.success("Opportunity removed"); refresh(); },
    onError: () => toast.error("Delete failed"),
  });
  const overdue = opp.visit_date && !opp.visit_done && opp.visit_date.slice(0, 10) < todayStr();

  return (
    <div className="soft-card anim-fade-up flex flex-col p-4" data-testid={`opp-card-${opp.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[15px] font-semibold text-navy">{opp.name}</div>
          <a href={`tel:${opp.phone}`} className="mt-0.5 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy">
            <Phone className="h-3 w-3" /> {opp.phone}
          </a>
        </div>
        <LeadTypeBadge type={opp.lead_type} />
      </div>

      <div className="mt-3 space-y-1.5 text-[13px] text-slate-600">
        <Row Icon={Home} value={opp.project} />
        <Row Icon={Wallet} value={opp.budget} />
        <div className={`flex items-center gap-1.5 ${overdue ? "font-semibold text-rose-600" : ""}`} data-testid={`opp-visit-date-${opp.id}`}>
          <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
          {opp.visit_date ? fmtLocal(opp.visit_date) : <span className="italic text-slate-400">No visit scheduled</span>}
          {overdue && <span className="text-[10px] uppercase">· overdue</span>}
        </div>
        {user?.role === "admin" && <Row Icon={UserIcon} value={opp.owner_name} />}
        {opp.lead_status && STATUS_META[opp.lead_status] && (
          <div className="flex items-center gap-1.5" data-testid={`opp-lead-status-${opp.id}`}>
            <span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[opp.lead_status].color }} /> {STATUS_META[opp.lead_status].label}
          </div>
        )}
      </div>
      {opp.visit_date && !opp.visit_done && (
        <button
          data-testid={`opp-reschedule-${opp.id}`}
          onClick={onReschedule}
          className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100"
        >
          <CalendarSync className="h-3.5 w-3.5" /> Reschedule
        </button>
      )}
      <VisitHistory history={opp.visit_history} testid={`opp-history-${opp.id}`} />
      {opp.remarks && <p className="mt-2 line-clamp-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-[12px] text-slate-600">{opp.remarks}</p>}

      <div className="mt-auto flex items-center justify-between gap-2 border-t border-dashed border-slate-100 pt-3 mt-3">
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
          <Switch
            data-testid={`opp-visit-toggle-${opp.id}`}
            checked={opp.visit_done}
            disabled={toggleMut.isPending}
            onCheckedChange={(v) => toggleMut.mutate(v)}
          />
          Visit Done
        </label>
        <div className="flex items-center gap-1">
          <Link to={`/leads/${opp.lead_id}`} data-testid={`opp-open-lead-${opp.id}`} className="grid h-8 w-8 place-items-center rounded-full text-slate-500 hover:bg-slate-100" aria-label="Open lead">
            <ExternalLink className="h-4 w-4" />
          </Link>
          <button data-testid={`opp-edit-${opp.id}`} onClick={onEdit} className="grid h-8 w-8 place-items-center rounded-full text-slate-500 hover:bg-slate-100" aria-label="Edit">
            <Pencil className="h-4 w-4" />
          </button>
          <button
            data-testid={`opp-delete-${opp.id}`}
            onClick={() => window.confirm(`Remove opportunity for ${opp.name}?`) && delMut.mutate()}
            className="grid h-8 w-8 place-items-center rounded-full text-rose-500 hover:bg-rose-50"
            aria-label="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ Icon, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 text-slate-400" /> {value}
    </div>
  );
}
